/**
 * HOW MANY UNSAVED CHANGES — worked out from the DIFFERENCE between the unsaved edits and the last save,
 * never counted as the chat applies edits, so changes made through the form count too and Save, Discard
 * and Undo reset it by themselves (spec, section 4). Glossary: Unsaved edits, Unsaved change.
 * Sample rows count as one change when they differ (plan 3b final review).
 * ⚠ `import type` only — see rules/entity-form-rules.ts.
 */
import type { Entity, TypeData } from '../../../../model/entity.model';
import { isFinalized, isSystemField } from '../rules/entity-form-rules';

/** The settings a builder can change on a field. Anything else (Atlas ids, key order) is not a change. */
const SETTINGS = [
  'name', 'type', 'defaultValue', 'defaultMode', 'description',
  'referenceEntityName', 'extendedAttributes', 'isUnique', 'primaryKey',
] as const;

/** JSON with object keys sorted, so a value read back from Firestore in another key order is still equal. */
const stable = (v: unknown): string =>
  JSON.stringify(v, (_k, x) => (x && typeof x === 'object' && !Array.isArray(x)
    ? Object.fromEntries(Object.entries(x).sort(([a], [b]) => a.localeCompare(b)))
    : x));

/** A field's identity across edits: its id, or — for system fields, which have none — its name. */
const keyOf = (f: TypeData): string => f.id || `name:${f.name}`;

const sameSettings = (a: TypeData, b: TypeData): boolean =>
  SETTINGS.every((k) => stable((a as any)[k] ?? null) === stable((b as any)[k] ?? null));

/** A builder's own fields, nested lists included. System fields never count. */
function userFields(data: readonly TypeData[] | undefined): number {
  return (data ?? []).reduce(
    (n, f) => n + (isSystemField(f.name) ? 0 : 1 + (f.type === 'ARRAY_OF_OBJECT' ? userFields(f.data) : 0)),
    0,
  );
}

function diff(before: readonly TypeData[], after: readonly TypeData[]): number {
  const prev = new Map(before.map((f) => [keyOf(f), f]));
  let n = 0;
  for (const f of after) {
    const p = prev.get(keyOf(f));
    if (!p) {
      if (!isSystemField(f.name)) n += 1 + (f.type === 'ARRAY_OF_OBJECT' ? userFields(f.data) : 0);
      continue;
    }
    prev.delete(keyOf(f));
    if (!isSystemField(f.name) && !sameSettings(p, f)) n += 1;
    if (f.type === 'ARRAY_OF_OBJECT' || p.type === 'ARRAY_OF_OBJECT') n += diff(p.data ?? [], f.data ?? []);
  }
  for (const p of prev.values()) if (!isSystemField(p.name)) n += 1; // removed
  return n;
}

/** `saved` = the entity as last saved, or null for one that was never saved (it counts itself + its fields). */
export function countUnsavedChanges(saved: Entity | null, current: Entity): number {
  // Was (2026-10-04, plan 6 — a new entity's sample rows were never counted, so 3 rows on a never-saved
  // entity said "2 unsaved" and were lost without Save):
  // if (!saved) return 1 + userFields(current.data);
  // Plan 6 — a new entity's sample rows are lost without Save, so they count (as one change, like a saved entity's).
  if (!saved) return 1 + userFields(current.data) + ((current.predefinedData ?? []).length ? 1 : 0);
  // Final review (2026-10-03, J2) — hand-edited sample rows count as one change when they differ; a
  // missing list and an empty one are the same (the page's getter adds [] for a missing list).
  const rows = (e: Entity): string => stable(e.predefinedData ?? []);
  return diff(saved.data ?? [], current.data ?? []) + (saved.name !== current.name ? 1 : 0)
    + (rows(saved) !== rows(current) ? 1 : 0);
}

/**
 * What leaving the open entity would lose, or null when nothing. A new entity counts itself as one change so the
 * chat can say so, but one the builder has neither named nor filled holds nothing, so leaving it needs no warning.
 */
export function whatLeavingLoses(saved: Entity | null, current: Entity): { count: number; name: string } | null {
  const count = countUnsavedChanges(saved, current);
  if (count === 0) return null;
  if (!saved && count === 1 && !current.name?.trim()) return null;
  return { count, name: current.name || 'The new entity' };
}

/** Spec section 1 (owner-approved): the first reminder at 5 unsaved changes, then every 3. */
export const REMINDER_FIRST = 5;
export const REMINDER_EVERY = 3;

/** `lastRemindedAt` = the count when the chat last reminded, or null if it has not this time. */
export function reminderDue(count: number, lastRemindedAt: number | null): boolean {
  if (count < REMINDER_FIRST) return false;
  return lastRemindedAt === null || count >= lastRemindedAt + REMINDER_EVERY;
}

/** The page's visible button (Q21): Save for New and Local, Update once finalized. */
export function saveActionFor(entity: Pick<Entity, 'status'>): 'Save' | 'Update' {
  return isFinalized(entity.status) ? 'Update' : 'Save';
}
